import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | false | null>(null);

  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Don't add token to auth endpoints (except /auth/me)
    if (this.isAuthEndpoint(request.url) && !request.url.includes('/auth/me')) {
      return next.handle(request);
    }

    const token = this.authService.getAccessToken();

    if (token) {
      request = this.addToken(request, token);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && !this.isAuthEndpoint(request.url)) {
          return this.handle401Error(request, next);
        }
        return throwError(() => error);
      })
    );
  }

  private addToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  private isAuthEndpoint(url: string): boolean {
    return url.includes('/auth/login') ||
           url.includes('/auth/register') ||
           url.includes('/auth/refresh') ||
           url.includes('/auth/logout') ||
           url.includes('/auth/forgot-password') ||
           url.includes('/auth/reset-password') ||
           url.includes('/auth/providers') ||
           url.includes('/auth/google/') ||
           url.includes('/auth/mfa/');
  }

  private handle401Error(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap(response => {
          this.isRefreshing = false;
          if (response.tokens) {
            this.refreshTokenSubject.next(response.tokens.accessToken);
            return next.handle(this.addToken(request, response.tokens.accessToken));
          }
          this.refreshTokenSubject.next(false);
          this.authService.handleSessionExpiry(this.router.url);
          return throwError(() => new Error('Token refresh failed'));
        }),
        catchError((error: HttpErrorResponse) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(false);
          if (error.status === 401 || error.status === 403) {
            this.authService.handleSessionExpiry(this.router.url);
          }
          return throwError(() => error);
        })
      );
    }

    return this.refreshTokenSubject.pipe(
      filter((token): token is string | false => token !== null),
      take(1),
      switchMap(token => {
        if (token === false) {
          return throwError(() => new Error('Session expired'));
        }
        return next.handle(this.addToken(request, token));
      })
    );
  }
}
